import { Column, Row } from 'src/shared/components/Semantic/all'
import { Image } from 'src/shared/components/ui'
import { ProductImage } from 'src/shared/components/ProductImage'
import { Copy } from 'lucide-react'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useOrganizationCurrency } from 'src/shared/hooks/useOrganizationCurrency'
import { toast } from 'src/shared/hooks/use-toast'
import { IMAGE_URLS } from 'src/assets/urlLinks'

export type RelatedProduct =
  | {
      id?: number | null
      article_number?: string | null
      item_name?: string | null
      price?: number | null
      moq?: number | null
      product_variant?: Array<{
        product_variant_color?: Array<{
          product_image?: Array<{ image_url?: string | null }>
        }>
      }>
    }
  | any

export const KlayderRecommendsSection = ({
  klayder_recommends,
  klayder_recommendation_text,
  related_products,
  onRelatedProductClick,
  variant = 'mobile',
}: {
  klayder_recommends: boolean
  klayder_recommendation_text?: string
  related_products?: RelatedProduct[]
  onRelatedProductClick?: (productId: number) => void
  variant?: 'mobile' | 'desktop'
}) => {
  const { t } = useLocalization()
  const currency = useOrganizationCurrency()

  if (!klayder_recommends) {
    return null
  }

  const padding = variant === 'mobile' ? 'p-3' : 'p-4'
  const gap = variant === 'mobile' ? 'gap-2' : 'gap-4'
  const textSize = variant === 'mobile' ? 'text-xs' : 'text-sm'
  const badgeGap = variant === 'mobile' ? 'gap-1' : 'gap-2'
  const badgePadding = variant === 'mobile' ? 'px-2 py-1.5' : 'px-2 py-1.5'
  const contactGap = variant === 'mobile' ? 'gap-2' : 'gap-3'
  const contactPadding = variant === 'mobile' ? 'p-2' : 'p-3'
  const mt = variant === 'mobile' ? 'mt-2' : 'mt-2'

  const recommendationText = klayder_recommendation_text?.split('||') || [
    '',
    '',
  ]

  return (
    <Column
      className={`${gap} ${padding} rounded-lg border border-gray-200 bg-gradient-to-br from-[#fffefc] to-[#f4f2e3] ${mt}`}
    >
      <div
        className={`${badgePadding} bg-white/80 rounded-full flex items-center ${badgeGap} w-fit border border-gray-200`}
      >
        <Image
          src={IMAGE_URLS.logos.klayderSymbol}
          alt="Klayder Symbol"
          className="w-4 h-4 invert"
        />
        <p
          className={`${textSize} bg-gradient-to-r from-blue-500 to-purple-500 text-transparent bg-clip-text text-normal`}
        >
          {t('Klayder recommends', 'Klayder rekommenderar')}
        </p>
      </div>

      {klayder_recommendation_text && (
        <p className={`${textSize} font-normal`}>
          {t(recommendationText[0], recommendationText[1])}
        </p>
      )}

      {(related_products?.length ?? 0) > 0 && (
        <>
          {variant === 'desktop' && (
            <h3 className="text-sm font-semibold">
              {t('Related products', 'Relaterade produkter')}
            </h3>
          )}
          <Row
            className={`grid grid-cols-2 ${
              variant === 'mobile' ? 'gap-2' : 'gap-3'
            }`}
          >
            {related_products?.map((product) => (
              <Column
                className={`w-full ${
                  variant === 'desktop' ? 'cursor-pointer' : ''
                }`}
                key={product.id}
                onClick={() => {
                  if (
                    variant === 'desktop' &&
                    product.id &&
                    onRelatedProductClick
                  ) {
                    onRelatedProductClick(product.id)
                  }
                }}
              >
                <ProductImage
                  imageClassName={'h-[160px] object-contain rounded-sm'}
                  containerClassName={'rounded-md'}
                  image_url={
                    product.product_variant?.[0]?.product_variant_color?.[0]
                      ?.product_image?.[0]?.image_url || ''
                  }
                  product_id={product.id || undefined}
                />
                <Row
                  className={`items-center gap-1 mt-1.5 ${
                    variant === 'mobile' ? 'cursor-pointer' : ''
                  }`}
                  onClick={(e) => {
                    if (variant === 'desktop') {
                      e.stopPropagation()
                    }
                    navigator.clipboard.writeText(product.article_number ?? '')
                    toast({
                      title: 'Article number copied to clipboard',
                      variant: 'success',
                      duration: 2000,
                    })
                  }}
                >
                  <Copy className="w-2.5 h-2.5" />
                  <p className="text-xs font-medium">{product.item_name}</p>
                </Row>
                <Row className="gap-1">
                  <p className="text-xs text-foreground/60">
                    {product.price?.toLocaleString() || '-'} {currency}
                  </p>
                  <p className="text-xs text-foreground/60">
                    ({product.moq} MOQ)
                  </p>
                </Row>
              </Column>
            ))}
          </Row>
        </>
      )}

      <Row
        className={`${contactGap} bg-white/80 ${contactPadding} rounded-lg border border-gray-200 items-center ${
          variant === 'mobile' ? 'mt-4' : 'mt-2'
        }`}
      >
        <div className="relative flex-shrink-0">
          <Image
            src="https://i.ibb.co/nqGYMRJ8/a4047548-c025-49cb-8953-9f42fc35763e.png"
            className="h-[50px] rounded-full border"
          />
        </div>
        <Column>
          <p className={`${textSize} font-normal`}>Carmelo Eismann</p>
          <p className="text-xs">(+46) 73 618 08 07</p>
        </Column>
      </Row>
    </Column>
  )
}
